import { useState } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Send, CreditCard, Zap, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const PAYMENT_METHODS = [
  { id: 'comdata', label: 'Comdata', needsCode: true, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  { id: 'efs', label: 'EFS', needsCode: true, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
  { id: 'fuel_card', label: 'Fuel Card', needsCode: false, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
  { id: 'zelle', label: 'Zelle', needsCode: false, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
];

export default function FuelAdvanceApprovalModal({ advance, onClose, onApproved }) {
  const [method, setMethod] = useState(null);
  const [moneyCode, setMoneyCode] = useState('');
  const [sending, setSending] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  const [loadFetched, setLoadFetched] = useState(false);

  // Fetch the load's PO/Pickup number
  useState(() => {
    if (advance?.load_id) {
      client.entities.Load.filter({ id: advance.load_id }, '-created_date', 1)
        .then(results => {
          if (results.length > 0) setPoNumber(results[0].po_pickup_number || '');
          setLoadFetched(true);
        })
        .catch(() => setLoadFetched(true));
    }
  }, [advance?.load_id]);

  const selectedMethod = PAYMENT_METHODS.find(m => m.id === method);

  const buildMessage = () => {
    const codeType = method === 'efs' ? 'THIS IS AN EFS CODE' : 'THIS IS A COMDATA CODE';
    if (selectedMethod?.needsCode) {
      return `You've been issued a MoneyCode for $${advance.amount.toFixed(2)}

Here is your code: ${moneyCode}


RATE CONF (Load #${advance.load_number})
PO (U Number): ${poNumber || '—'}


${codeType}`;
    }
    return `Your fuel advance of $${advance.amount.toFixed(2)} for Load #${advance.load_number} has been approved via ${selectedMethod?.label}.`;
  };

  const handleSend = async () => {
    if (!method) { toast.error('Please select a payment method.'); return; }
    if (selectedMethod?.needsCode && !moneyCode.trim()) { toast.error('Please enter the MoneyCode.'); return; }

    setSending(true);
    const today = new Date().toISOString().split('T')[0];

    // 1. Approve the advance
    await client.entities.FuelAdvance.update(advance.id, {
      status: 'Approved',
      approved_date: today,
      notes: `Issued via ${selectedMethod?.label}${selectedMethod?.needsCode ? ` — Code: ${moneyCode}` : ''}`,
    });

    // 2. Post message to load chat
    const message = buildMessage();
    await client.entities.LoadMessage.create({
      load_id: advance.load_id,
      load_number: advance.load_number,
      sender_email: 'dispatch@system',
      sender_name: 'Dispatcher',
      sender_role: 'admin',
      message,
    });

    // 3. Email the driver if they have an email
    if (advance.driver_email) {
      try {
        await client.integrations.Core.SendEmail({
          to: advance.driver_email,
          subject: `Fuel Advance Issued — Load #${advance.load_number}`,
          body: message.replace(/\n/g, '<br>'),
        });
      } catch (_) {
        // email failure is non-blocking
      }
    }

    toast.success('Fuel advance approved & message sent!');
    setSending(false);
    onApproved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-md z-10 p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-space font-semibold text-foreground">Approve Fuel Advance</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {advance.driver_name} — <span className="text-primary font-bold">${advance.amount?.toFixed(2)}</span> — Load #{advance.load_number}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Payment Method Selection */}
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Select Payment Method</p>
          <div className="grid grid-cols-2 gap-3">
            {PAYMENT_METHODS.map(m => (
              <button
                key={m.id}
                onClick={() => { setMethod(m.id); setMoneyCode(''); }}
                className={cn(
                  "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all font-semibold text-sm",
                  method === m.id
                    ? `${m.bg} ${m.border} ${m.color}`
                    : 'border-border bg-muted/40 text-muted-foreground hover:border-border hover:text-foreground'
                )}
              >
                <CreditCard className="w-5 h-5" />
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* MoneyCode Input (Comdata / EFS only) */}
        {selectedMethod?.needsCode && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Enter {selectedMethod.label} MoneyCode
            </p>
            <input
              type="text"
              value={moneyCode}
              onChange={e => setMoneyCode(e.target.value)}
              placeholder="e.g. 123456789"
              className="w-full px-3 py-2.5 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 font-mono"
            />
          </div>
        )}

        {/* Message Preview */}
        {method && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Message Preview</p>
            <pre className="text-xs text-foreground/80 bg-muted/40 border border-border rounded-xl p-3 whitespace-pre-wrap leading-relaxed font-mono">
              {buildMessage()}
            </pre>
            {advance.driver_email && (
              <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                <Zap className="w-3 h-3 text-primary" />
                Will also email <span className="text-primary">{advance.driver_email}</span>
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button onClick={onClose} variant="outline" className="border-border flex-1" disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !method}
            className="flex-1 bg-primary text-primary-foreground"
          >
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Approve & Send</>}
          </Button>
        </div>
      </div>
    </div>
  );
}